import React, { ReactNode, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { FieldRenderProps } from 'react-final-form';
import { ChromePicker, ColorResult } from 'react-color';

import './ColorPickerField.styles.scss';

export type ColorPickerFieldProps = FieldRenderProps<string, HTMLElement> & {
  label?: string;
  description?: ReactNode;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  showErrorMsg?: boolean;
  customOnChange?: (value: string, isValid: boolean) => void;
};

const ColorPickerField: React.FC<ColorPickerFieldProps> = (props) => {
  const {
    label,
    description,
    input: { onChange, onBlur, value, ...restInput },
    meta: { touched, error },
    required,
    className,
    disabled,
    showErrorMsg = true,
    customOnChange,
  } = props;

  const [displayColorPicker, setDisplayColorPicker] = useState(false);

  const hasError = useMemo(() => touched && error, [error, touched]);

  const classes = classNames(
    'color-picker-field',
    {
      'color-picker-field--error': hasError,
      'color-picker-field--disabled': disabled,
    },
    className,
  );

  const handleClick = useCallback(() => {
    if (!disabled) {
      setDisplayColorPicker((prev) => !prev);
    }
  }, [disabled]);

  const handleClose = useCallback(() => {
    setDisplayColorPicker(false);
    onBlur();
  }, [onBlur]);

  const handleChange = useCallback(
    (color: ColorResult) => {
      const hexValue = color.hex;
      onChange(hexValue);
      customOnChange?.(hexValue, !hasError);
    },
    [onChange, customOnChange, hasError],
  );

  return (
    <div className={classes}>
      {label && <label className={required ? 'required' : ''}>{label}</label>}

      <div className="color-picker-field__swatch" onClick={handleClick}>
        <div
          className="color-picker-field__color"
          style={{ background: value || '#ffffff' }}
        />
      </div>

      {displayColorPicker && (
        <div className="color-picker-field__popover">
          <div className="color-picker-field__cover" onClick={handleClose} />
          <ChromePicker
            {...restInput}
            color={value || '#ffffff'}
            onChange={handleChange}
            disableAlpha
          />
        </div>
      )}

      {showErrorMsg && hasError && (
        <p className="color-picker-field__error-title">{error}</p>
      )}
      {!hasError && description && (
        <p className="color-picker-field__description">{description}</p>
      )}
    </div>
  );
};

export default ColorPickerField;
